import React, { useState } from 'react';
import {
  Typography,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import BaseModal from './BaseModal';
import { CustomButton } from '../Infra/CustomButton/CustomButton';

interface ResetConfigModalProps {
  open: boolean;
  onClose: () => void;
  onReset: (tableName: string) => void;
  availableTables: string[];
}

const ResetConfigModal: React.FC<ResetConfigModalProps> = ({
  open,
  onClose,
  onReset,
  availableTables,
}) => {
  const [selectedTable, setSelectedTable] = useState('');
  const [confirmationStep, setConfirmationStep] = useState(false);

  const handleReset = () => {
    if (!selectedTable) return;

    if (!confirmationStep) {
      setConfirmationStep(true);
      return;
    }

    onReset(selectedTable);
    handleClose();
  };

  const handleClose = () => {
    setSelectedTable('');
    setConfirmationStep(false);
    onClose();
  };

  const customFooter = (
    <>
      <CustomButton
        onClick={handleClose}
        variant="outlined"
        sx={{ minWidth: 120, height: 40 }}
      >
        Cancel
      </CustomButton>
      {!confirmationStep ? (
        <CustomButton
          onClick={handleReset}
          variant="outlined"
          disabled={!selectedTable}
          startIcon={<DeleteIcon />}
          sx={{ minWidth: 120, height: 40 }}
        >
          Reset Configuration
        </CustomButton>
      ) : (
        <CustomButton
          onClick={handleReset}
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          sx={{ minWidth: 120, height: 40 }}
        >
          Confirm Delete
        </CustomButton>
      )}
    </>
  );

  return (
    <BaseModal
      dataTestId="reset-config-modal"
      open={open}
      onClose={handleClose}
      title="Reset Table Configuration"
      icon={<WarningIcon color="warning" />}
      customFooter={customFooter}
      maxWidth="sm"
    >
      <Stack spacing={3}>
        {!confirmationStep ? (
          <>
            <Alert severity="warning">
              This will permanently delete all field mappings and
              transformations for the selected table. This action cannot be
              undone.
            </Alert>

            <FormControl fullWidth>
              <InputLabel>Select Table to Reset</InputLabel>
              <Select
                value={selectedTable}
                label="Select Table to Reset"
                onChange={e => setSelectedTable(e.target.value)}
              >
                {availableTables.map(table => (
                  <MenuItem key={table} value={table}>
                    {table}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {availableTables.length > 0 && (
              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Available Tables:
                </Typography>
                <List dense>
                  {availableTables.map(table => (
                    <ListItem key={table} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={table}
                        secondary={`Configuration will be completely removed`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </>
        ) : (
          <>
            <Alert severity="error">
              <Typography variant="h6" sx={{ mb: 1 }}>
                Final Confirmation Required
              </Typography>
              You are about to permanently delete the configuration for:
            </Alert>

            <Paper
              sx={{
                p: 3,
                bgcolor: 'error.50',
                border: 2,
                borderColor: 'error.200',
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  textAlign: 'center',
                  color: 'error.main',
                  fontWeight: 'bold',
                }}
              >
                {selectedTable}
              </Typography>
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', mt: 1, color: 'error.dark' }}
              >
                All field mappings, transformations, and configurations will be
                lost
              </Typography>
            </Paper>

            <Alert severity="info">
              <Typography variant="body2">
                <strong>What will be deleted:</strong>
                <br />• All column mappings for this table
                <br />• All text matching configurations
                <br />• All math operation configurations
                <br />• All date format configurations
                <br />• All extra configuration settings
              </Typography>
            </Alert>
          </>
        )}
      </Stack>
    </BaseModal>
  );
};

export default ResetConfigModal;
